import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Grid, MenuItem, Paper } from '@mui/material';
import Title from 'components/common/Title';
import { Form } from 'react-final-form';
import { Select, TextField } from 'mui-rff';
import { formatRoleToLabel, handleApiError } from 'utils/common';
import { createSchemaValidator } from 'utils/validate';
import {
  CREATE_USER_VALIDATION_SCHEMA,
  UPDATE_USER_VALIDATION_SCHEMA,
} from 'utils/validation.schema';
import { useSnackbar } from 'notistack';
import {
  useGetCurrentUserQuery,
  useGetRolesDropdownQuery,
  useGetUserByIdQuery,
  useUpdateUserMutation,
} from '../../../store/api/user.api';

export default function UserEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: { id: currentUserId },
  } = useGetCurrentUserQuery();
  const { data: user = {}, isLoading: loadingUser } = useGetUserByIdQuery(id, {
    skip: !id,
  });
  const initialValues = useMemo(
    () => _.pick(user, ['name', 'email', 'role']),
    [user],
  );
  const { data: roles = [], isLoading: loadingRoles } =
    useGetRolesDropdownQuery();
  const [mutateAsync] = useUpdateUserMutation();
  const { enqueueSnackbar } = useSnackbar();
  const validationFn = useMemo(
    () =>
      createSchemaValidator(
        id ? UPDATE_USER_VALIDATION_SCHEMA : CREATE_USER_VALIDATION_SCHEMA,
      ),
    [id],
  );

  const submitForm = useCallback(
    async (values) => {
      try {
        const payload = {
          ...values,
          ...(id ? { id } : {}),
        };
        await mutateAsync(payload).unwrap();
        enqueueSnackbar('Success', { variant: 'success' });
        if (!id) {
          navigate('/users');
        }
      } catch (err) {
        handleApiError(err, 'Error');
      }
    },
    [mutateAsync, enqueueSnackbar, navigate, id],
  );

  if (loadingRoles || loadingUser) {
    return <></>;
  }
  return (
    <Paper sx={{ mx: '32px', p: '32px' }}>
      <Title>User details</Title>
      <Form
        onSubmit={submitForm}
        initialValues={initialValues}
        validate={validationFn}
        validateOnBlur={false}
        render={({ handleSubmit, submitting, values }) => (
          <form onSubmit={handleSubmit} autoComplete='off'>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField name='name' autoComplete='off' label='User Name' />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField name='email' autoComplete='off' label='User Email' />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Select
                  name='role'
                  label='User Role'
                  inputLabelProps={{ sx: { ml: '-14px' } }}
                  formHelperTextProps={{ sx: { ml: 0 } }}
                >
                  {roles.map(({ value }) => (
                    <MenuItem key={value} value={value}>
                      {formatRoleToLabel(value)}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} />
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name='password'
                  autoComplete='new-password'
                  type='password'
                  label='Password'
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name='confirmPassword'
                  autoComplete='off'
                  type='password'
                  label='Confirm password'
                />
              </Grid>
            </Grid>

            {user.id === currentUserId && user.role !== values['role'] ? (
              <Alert variant='outlined' sx={{ mt: '32px' }} severity='warning'>
                You have changed your own role from{' '}
                <b>{formatRoleToLabel(user.role)}</b> to{' '}
                <b>{formatRoleToLabel(values['role'])}</b>, which might lock you
                out of some functionality
              </Alert>
            ) : null}
            <Box display='flex' gap='16px' mt='32px'>
              <Button
                variant='outlined'
                component={Link}
                to='/users'
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button variant='contained' type='submit' disabled={submitting}>
                Save
              </Button>
            </Box>
          </form>
        )}
      />
    </Paper>
  );
}
